/**
 * Unified Login helper functions
 * Contains logic for initializing and managing unified login flows
 */
import fetch from 'globals/wa-fetch';
import { lookupKeys, performTenancyLookup } from 'globals/regional-api';
import { setUser } from 'stores/user-store';
import { fetchCompany, setCompany } from 'stores/company-store';
import { updateFeaturesList } from 'state/features-list/actions';
import { getStore } from 'hermes-redux';
import { storageKeys, set as setStorage } from 'globals/storage';
import { logout, updateUser } from 'state/session/actions';
import { setSessionLoadingCompleted } from 'app/state/session/actions';
import { goTo, getUrlParameter, getSubdomainFromHost } from 'router/navigation';
import { runOnWebWorker } from 'app/run-on-web-worker';
import { startPostLoginFlow } from 'state/post-login/actions';
import { SIGNUP_CODES } from 'pages/signup/constants';
import { updateData } from 'pages/signup/main/state/actions';
import { AppLogger } from '@workivate/tho-web-shared';
import queryString from 'query-string';
import { shouldResumePostLoginFlow } from 'app/state/consent/actions';
import Language from 'language';

import { setWonderingLocale } from 'globals/wondering/helpers';

type RegionEnabledFeatures = {
  is_unified_login_enabled: boolean;
  is_unified_sso_sign_up_enabled: boolean;
};

type RegionData = { region: string; enabled_features: RegionEnabledFeatures };

export const handleLangUpdateDuringLogin = async user => {
  const langQueryString = getUrlParameter('lang');
  const { dispatch } = getStore();

  // By default setLoginLanguagePersistence is set to false unless the user changes language
  // If the 'shouldPersistLoginPageLanguage' storage value is true we will need to make an API call to permanently set the user locale
  // And reload translations
  if (Language.getLoginLanguagePersistence()) {
    // If the stored local is not the same as the user locale, we need to update it (unless it's a shared account)
    if (Language.getLocale() !== user.locale) {
      // For all user types except for shared users we should update the user locale - See UM-1695 for the original issue
      if (!user.is_shared_account) {
        dispatch(updateUser({ locale: Language.getLocale() }, false, false));
      }

      Language.loadTranslations(Language.getLocale());
    }
  } else {
    // If the locale should not be persisted, we will perform a one-time update
    await Language.set({
      locale: langQueryString || user.locale,
      isSharedAccount: user.is_shared_account,
      shouldLoadAllTranslations: true,
    });
  }
  setWonderingLocale(user.locale);
};

/**
 * Initialises the login process from an app perspective
 * Sets user and company into the redux store
 */
export const initUnifiedLogin = async () => {
  const { dispatch } = getStore();
  // Get user info, set the user in the redux store and fetch company info
  try {
    const { body: user } = await fetch('/user/me?exclude_field=badges', {
      body: undefined,
      method: 'get',
      oauth2token: true,
    });

    // This is for porting over THVC logic and can be removed once sign up is fully migrated to USL: UI-4150
    // It is also used if the user needs to complete the signup flow,
    // we need to store the original url in order to redirect the user back to the original page after signup
    setStorage(storageKeys.signupReturnUrl, window.location.pathname, false);

    const company = await fetchCompany(user.company_id);
    setCompany(company.body);

    await handleLangUpdateDuringLogin(user);

    // Although the features list we pass here is used to build the menu
    // the permissions for the menu are checked against the user object from the Redux store
    // This could perhaps be improved when we come to remove the legacy login
    // as it is not obvious that we need to both set the user and THEN pass the same features list to rebuild the menu
    await setUser(user);
    if (user.features_list.length !== 0) {
      await dispatch(updateFeaturesList(user.features_list));
    }

    if (user['signup_state'] === SIGNUP_CODES.SSO_SIGNUP_PROVISIONED) {
      // If first login from sso, the signup flow needs to be completed
      dispatch(
        updateData('initData', {
          user,
          signup_state: user['signup_state'],
        }),
      );

      await dispatch(shouldResumePostLoginFlow(true)); // we don't want to start the flow yet
      // because we need to complete the signup flow before showing consents or walkthrough
      goTo('signup');
    } else {
      runOnWebWorker(startPostLoginFlow);
    }
  } catch (error) {
    AppLogger.error('Error during initUnifiedLogin', error);
    await dispatch(logout(false));
  }

  await dispatch(setSessionLoadingCompleted());
};

/**
 * Sets the value to localStorage
 * @param isUnifiedLoginEnabled
 */
export const setUnifiedLoginEnabled = (isUnifiedLoginEnabled: boolean) => {
  localStorage.setItem(
    storageKeys.isUnifiedLoginEnabled,
    JSON.stringify(isUnifiedLoginEnabled),
  );
};

export const getUslEnabledFromRegionApi = async (): Promise<boolean> => {
  try {
    const response: RegionData = await fetch(
      `https://${window.WAM.ENV.awsCognitoSSODomain}/region`,
      { method: 'GET', token: false },
      true,
      false,
      false,
    );

    if (!response) {
      throw new Error('Region API response is empty');
    }
    const is_unified_login_enabled =
      response?.enabled_features?.is_unified_login_enabled;
    return !!is_unified_login_enabled;
  } catch (e) {
    AppLogger.error(
      'Error while requesting region API during app initialization',
      e,
    );
    return false;
  }
};

/**
 * If we are in a production environment, we will always use the value from the region API.
 * If we are in a non-production environment, we will check if the URL parameter 'isUnifiedLoginEnabled' exists
 * If there is no URL parameter, we will use a LocalStorage value

 * If the API call fails, we will assume that USL is disabled.
 */
export const getIsUnifiedLoginEnabled = async (): Promise<boolean> => {
  const urlParams = queryString.parse(location.search, { parseBooleans: true });
  const isUnifiedLoginEnabledParam = urlParams['isUnifiedLoginEnabled'];

  let effectiveIsUnifiedLoginEnabled = true;
  try {
    // getUslEnabledFromRegionApi already returns false on API failure
    const apiUslEnabled = await getUslEnabledFromRegionApi();
    if (IS_PRODUCTION) {
      // In production, we only use the API value
      return apiUslEnabled;
    }
    effectiveIsUnifiedLoginEnabled = apiUslEnabled;

    // Check if we have a URL parameter that overrides the value from the region API
    if (isUnifiedLoginEnabledParam !== undefined) {
      const paramValue =
        isUnifiedLoginEnabledParam === 'true' ||
        isUnifiedLoginEnabledParam === '"true"' ||
        isUnifiedLoginEnabledParam === "'true'";

      return paramValue;
    }
    // If no URL parameter, check localStorage
    const localStorageUslEnabled = localStorage.getItem(
      storageKeys.isUnifiedLoginEnabled,
    );

    if (localStorageUslEnabled !== null) {
      effectiveIsUnifiedLoginEnabled =
        localStorageUslEnabled === 'true' ||
        localStorageUslEnabled === '"true"' ||
        localStorageUslEnabled === "'true'";
    }
    return effectiveIsUnifiedLoginEnabled;
  } catch (e) {
    // If anything fails in this function, assume USL is not enabled
    AppLogger.error(
      'Error in getIsUnifiedLoginEnabled, defaulting to USL disabled',
      e,
    );
    return false;
  }
};

/**
 * Performs the tenancy lookup based on the provided key and value
 * The response is used to set the correct regional API endpionts for further requests
 * This is why we want to ensure that the tenancy lookup is performed before any other API calls
 */
export const callTenancyLookup = async () => {
  const { dispatch } = getStore();

  try {
    const tenancyHash = localStorage.getItem(lookupKeys.tenancyHash);
    let lookUpResponse;

    // Make a call to the tenancy lookup API to set the correct endpoints
    if (tenancyHash) {
      lookUpResponse = await performTenancyLookup(
        lookupKeys.tenancyHash,
        tenancyHash,
      );
    } else {
      // If tenancyHash is not set, we will use the domain to perform the lookup
      lookUpResponse = await performTenancyLookup(
        lookupKeys.domain,
        getSubdomainFromHost(),
      );
    }
    if (!lookUpResponse) {
      AppLogger.error('Tenancy lookup failed, no response received');
      await dispatch(logout(false));
    }
  } catch (error) {
    AppLogger.error('Error in performTenancyLookup', error);
    await dispatch(logout(false));
  }
};
